// Misti's voice for lines sent from the phone app ("eng:..." / {type: "say"}).
// That path is separate from the Dograh conversation, which speaks for itself.
//
// The robot makes the audio: its backend (Misti_HW, POST /tts) voices each line
// with Sarvam, keeps it, and hands it over. Saved messages are voiced there as
// soon as they are saved, so they play at once; a typed line is made on the spot
// and kept too. The Sarvam key stays on the robot - it never reaches a browser.
//
// Fallback, for a developer machine with no robot backend: REACT_APP_TTS_PROXY_URL,
// or REACT_APP_SARVAM_API_KEY directly. SECURITY: a REACT_APP_ key is baked into
// the page and readable by anyone who loads it - never set it on the public site.

import { robotUrl } from "services/robotUrl";

const SARVAM_TTS_URL = "https://api.sarvam.ai/text-to-speech";

/**
 * @param {string} text          what to say
 * @param {string} languageCode  "en-IN" | "bn-IN" | "hi-IN"
 * @returns {Promise<Blob>}      the audio, ready for an <audio> element
 */
export async function synthesizeSpeech(text, languageCode = "en-IN") {
	try {
		return await fromRobot(text, languageCode);
	} catch (err) {
		if (!process.env.REACT_APP_TTS_PROXY_URL && !process.env.REACT_APP_SARVAM_API_KEY) throw err;
		console.warn(`[voice] the robot could not voice this line (${err.message}) - trying the fallback`);
	}
	return base64ToBlob(await fromFallback(text, languageCode));
}

async function fromRobot(text, languageCode) {
	const controller = new AbortController();
	// A typed line is made on the spot; Sarvam can take a few seconds for a long one.
	const timer = setTimeout(() => controller.abort(), 35000);
	let res;
	try {
		res = await fetch(`${robotUrl()}/tts`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ text, language: languageCode }),
			signal: controller.signal,
		});
	} catch (err) {
		throw new Error(err.name === "AbortError" ? "the robot took too long to voice it" : `cannot reach the robot (${err.message})`);
	} finally {
		clearTimeout(timer);
	}
	if (!res.ok) {
		let why = `robot answered ${res.status}`;
		try {
			why = (await res.json()).error || why;
		} catch (e) {}
		throw new Error(why);
	}
	return res.blob();
}

async function fromFallback(text, languageCode) {
	const proxy = process.env.REACT_APP_TTS_PROXY_URL;
	if (proxy) {
		const res = await fetch(proxy, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ text, target_language_code: languageCode }),
		});
		if (!res.ok) throw new Error(`TTS proxy failed: ${res.status}`);
		const data = await res.json();
		return data.audios ? data.audios[0] : data.audio;
	}

	const key = process.env.REACT_APP_SARVAM_API_KEY;
	console.warn("Sarvam key is exposed to the browser. Let the robot voice lines instead (POST /tts).");
	const res = await fetch(SARVAM_TTS_URL, {
		method: "POST",
		headers: { "Content-Type": "application/json", "api-subscription-key": key },
		body: JSON.stringify({
			text,
			target_language_code: languageCode,
			model: "bulbul:v3",
			speaker: process.env.REACT_APP_SARVAM_VOICE || "priya",
		}),
	});
	if (!res.ok) throw new Error(`Sarvam TTS failed: ${res.status} ${await res.text()}`);
	const data = await res.json();
	return data.audios[0];
}

function base64ToBlob(b64) {
	const bytes = atob(b64);
	const out = new Uint8Array(bytes.length);
	for (let i = 0; i < bytes.length; i++) out[i] = bytes.charCodeAt(i);
	return new Blob([out], { type: "audio/wav" });
}

const sarvamTts = { synthesizeSpeech };

export default sarvamTts;
